import React, { useEffect } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ChevronDown, Home } from 'lucide-react';

const RoofCalculatorPanel = ({ isOpen, onToggle, roofData, setRoofData, onCalculate, measurementTotals, manualOverride, setManualOverride, onServiceTotalChange }) => {
  // Auto-fill from measurement if panel is open and user hasn't manually edited
  useEffect(() => {
    if (!isOpen) return;
    const area = measurementTotals?.areaSqFt || 0;
    // Only auto-fill if: area > 0 AND user hasn't manually overridden
    if (area > 0 && !manualOverride.roofSqFt) {
      // Round to whole number for clean UX display
      setRoofData((prev) => ({ ...prev, sqFt: Math.round(area) }));
    }
  }, [isOpen, measurementTotals?.areaSqFt, manualOverride.roofSqFt, setRoofData]);

  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <Card className="overflow-hidden">
        <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Home className="h-5 w-5 text-primary" />
            </div>
            <div className="text-left">
              <h3 className="text-lg font-bold text-secondary">🏠 Roof Cleaning</h3>
              <p className="text-sm text-muted-foreground">Configure roof specifications</p>
            </div>
          </div>
          <ChevronDown
            className={`h-5 w-5 text-muted-foreground transition-transform ${
              isOpen ? 'transform rotate-180' : ''
            }`}
          />
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="p-6 space-y-4 bg-muted/30">
            {/* Measured Area */}
            <div className="space-y-2">
              <Label htmlFor="roof-area">Measured Area (sq ft)</Label>
              <Input
                id="roof-area"
                type="number"
                placeholder="Enter square footage"
                value={roofData.sqFt}
                onChange={(e) => {
                  setManualOverride({ ...manualOverride, roofSqFt: true });
                  setRoofData({ ...roofData, sqFt: e.target.value });
                }}
                className="font-mono"
              />
            </div>

            {/* Material Type */}
            <div className="space-y-2">
              <Label htmlFor="roof-material">Material / Surface Type</Label>
              <Select
                value={roofData.material}
                onValueChange={(value) => setRoofData({ ...roofData, material: value })}
              >
                <SelectTrigger id="roof-material">
                  <SelectValue placeholder="Select material" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asphalt">Asphalt Shingles</SelectItem>
                  <SelectItem value="cedar">Cedar / Wood Shakes</SelectItem>
                  <SelectItem value="stone">Stone / Concrete / Slate</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Contamination Stage */}
            <div className="space-y-2">
              <Label htmlFor="roof-stage">Contamination Stage</Label>
              <Select
                value={roofData.stage}
                onValueChange={(value) => setRoofData({ ...roofData, stage: value })}
              >
                <SelectTrigger id="roof-stage">
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stage1">Stage 1 – Black Streaks/Green</SelectItem>
                  <SelectItem value="stage2">Stage 2 – Lichen</SelectItem>
                  <SelectItem value="stage3">Stage 3 – Moss</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Roof Pitch */}
            <div className="space-y-2">
              <Label htmlFor="roof-pitch">Roof Pitch</Label>
              <Select
                value={roofData.pitch}
                onValueChange={(value) => setRoofData({ ...roofData, pitch: value })}
              >
                <SelectTrigger id="roof-pitch">
                  <SelectValue placeholder="Select pitch" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1/2">1/2</SelectItem>
                  <SelectItem value="2/12">2/12</SelectItem>
                  <SelectItem value="3/12">3/12</SelectItem>
                  <SelectItem value="4/12">4/12</SelectItem>
                  <SelectItem value="5/12">5/12</SelectItem>
                  <SelectItem value="6/12">6/12</SelectItem>
                  <SelectItem value="7/12">7/12</SelectItem>
                  <SelectItem value="8/12">8/12</SelectItem>
                  <SelectItem value="9/12">9/12</SelectItem>
                  <SelectItem value="10/12">10/12</SelectItem>
                  <SelectItem value="11/12">11/12</SelectItem>
                  <SelectItem value="12/12">12/12</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Story Count */}
            <div className="space-y-2">
              <Label htmlFor="roof-stories">Story Count</Label>
              <Select
                value={roofData.stories}
                onValueChange={(value) => setRoofData({ ...roofData, stories: value })}
              >
                <SelectTrigger id="roof-stories">
                  <SelectValue placeholder="Select stories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">One Story</SelectItem>
                  <SelectItem value="2">Two Story</SelectItem>
                  <SelectItem value="3">Three or More Stories</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Calculate Button */}
            <Button
              onClick={async () => {
                if (typeof onCalculate === 'function') {
                  await onCalculate();
                }

                if (typeof onServiceTotalChange === 'function') {
                  const sqFt = Number(roofData.sqFt) || 0;
                  const result =
                    (typeof window !== 'undefined' &&
                      window.__LATEST_ROOF_TOTALS__) || null;

                  onServiceTotalChange('roof', {
                    input: {
                      sqFt,
                      pitch: roofData.pitch,
                      stage: roofData.stage,
                      stories: roofData.stories,
                      material: roofData.material,
                    },
                    result: result || null,
                    measurement: null,
                  });
                }
              }}
              className="w-full bg-primary hover:bg-primary/90"
              size="lg"
            >
              Calculate Roof Cost
            </Button>
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default RoofCalculatorPanel;